import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

type Tone = 'base' | 'card';

interface SectionProps extends HTMLAttributes<HTMLElement> {
  /** Section element id. Also the nav target. */
  id: string;
  /** Opaque base shade; alternate between sections for separation. */
  tone?: Tone;
  /**
   * The shell's own padding. A full-bleed section whose child owns the whole viewport (the
   * pinned hero stage) sets this to false; the child brings its own gutters.
   */
  padded?: boolean;
}

const toneClass: Record<Tone, string> = {
  base: 'bg-background',
  card: 'bg-card',
};

/** Content width used inside every section. */
export const shellClass = 'mx-auto w-full max-w-[1180px]';

/** Opaque, normal-flow section separated from the previous one by a hairline. */
export const Section = forwardRef<HTMLElement, SectionProps>(function Section(
  { id, tone = 'base', padded = true, className, children, ...rest },
  ref,
) {
  return (
    <section
      ref={ref}
      id={id}
      className={cn(
        'relative isolate overflow-hidden border-t border-border',
        padded && 'px-[clamp(1.4rem,5vw,5rem)] py-[clamp(5rem,12vw,10rem)]',
        toneClass[tone],
        className,
      )}
      {...rest}
    >
      {children}
    </section>
  );
});
